import type { ReactNode } from "react"
import { Breadcrumb } from "./breadcrumb"
import type { BreadcrumbTrail } from "@/lib/breadcrumbs"

interface Crumb {
  label: string
  href?: string
}

/** The hero's breadcrumb look — 11px uppercase, wide tracking, "/" separators. */
export const EDITORIAL_HERO_BREADCRUMB_CLASSES = {
  nav: "mb-6 text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold",
  list: "flex flex-wrap items-center gap-2 list-none m-0 p-0",
  item: "flex items-center gap-2",
  link: "hover:text-[#ff9951] transition-colors",
  current: "text-gray-900",
  separator: "text-gray-300",
} as const

interface EditorialHeroProps {
  /**
   * The page's breadcrumb trail from lib/breadcrumbs.ts — the same array the
   * page emits as its BreadcrumbList (FRIDGE-269). Preferred.
   */
  trail?: BreadcrumbTrail
  /** Legacy { label, href } trail; ignored when `trail` is given. */
  breadcrumb?: Crumb[]
  /**
   * Optional canonical page name. When provided, becomes the page H1
   * (rendered as the orange editorial eyebrow line) and the editorial
   * display text is demoted to H2 — preserving SEO/LLM signal alignment
   * with the URL slug and breadcrumb leaf. When omitted, the display
   * text remains the H1 (legacy behavior).
   */
  toolName?: string
  /** Uppercase orange eyebrow, rendered after a `◉` bullet (or under the H1 when toolName is set) */
  eyebrow: string
  /** Display title — pre-italic text */
  titlePre: ReactNode
  /** Italic, orange-accent word/phrase in the middle of the title */
  titleItalic: ReactNode
  /** Optional text that follows the italic portion of the title */
  titlePost?: ReactNode
  /** Subtitle paragraph, rendered under the title */
  subtitle?: ReactNode
  /** Optional metadata row (short strings joined with middots) */
  meta?: string[]
  /** Optional inline share bar rendered just under the meta row (e.g. <ToolShareBar /> instance) */
  share?: ReactNode
  /** Optional right-column content (card, callout, quick-answer panel) — renders on md+ */
  right?: ReactNode
  /** Additional classes on the outer section */
  className?: string
}

/**
 * EditorialHero — the magazine-style tool-page hero introduced with the
 * /tools index redesign and extended across every tool-page variant.
 *
 * Layout: breadcrumb + orange `◉ ` eyebrow + display H1 with italic orange
 * accent word + subtitle + optional meta row on the left; optional right
 * slot on md+ for a companion card (e.g. the "Quick answer" or "How it
 * works" panel on guide/quiz pages).
 *
 * Typography matches the /tools hero exactly — H1 clamps 44→80px. The H1
 * wraps in `text-wrap: balance` so headlines don't orphan a single word.
 */
export function EditorialHero({
  trail,
  breadcrumb,
  toolName,
  eyebrow,
  titlePre,
  titleItalic,
  titlePost,
  subtitle,
  meta,
  share,
  right,
  className = "",
}: EditorialHeroProps) {
  const DisplayTag = toolName ? "h2" : "h1"
  const crumbs: BreadcrumbTrail =
    trail ?? (breadcrumb ?? []).map((c) => ({ name: c.label, url: c.href }))
  const bc = EDITORIAL_HERO_BREADCRUMB_CLASSES

  return (
    <section className={`bg-white border-b border-gray-100 ${className}`}>
      <div className="max-w-[1320px] mx-auto px-6 md:px-8 pt-6 pb-8 md:pt-8 md:pb-10">
        {crumbs.length > 0 && (
          // The ONE visible breadcrumb renderer (list semantics, aria-current);
          // same look as before — the flex container moved onto the <ol>.
          <Breadcrumb
            trail={crumbs}
            className={bc.nav}
            listClassName={bc.list}
            itemClassName={bc.item}
            linkClassName={bc.link}
            currentClassName={bc.current}
            separatorClassName={bc.separator}
          />
        )}

        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr] items-end">
          <div>
            {toolName ? (
              <h1
                className="font-bold uppercase tracking-[0.12em] text-[#ff9951] mb-4"
                style={{
                  fontSize: "clamp(13px, 1.1vw, 15px)",
                  letterSpacing: "0.12em",
                  lineHeight: 1.2,
                  margin: 0,
                  marginBottom: 16,
                }}
              >
                {toolName}
              </h1>
            ) : (
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
                {eyebrow}
              </div>
            )}
            <DisplayTag
              className="font-black text-gray-900"
              style={{
                fontSize: "clamp(34px, 4.5vw, 60px)",
                letterSpacing: "-0.035em",
                lineHeight: 1.02,
                textWrap: "balance" as never,
                margin: 0,
              }}
            >
              {titlePre}
              {" "}
              <em className="italic font-black text-[#ff9951]">{titleItalic}</em>
              {titlePost ? <> {titlePost}</> : null}
            </DisplayTag>
            {subtitle && (
              <p
                className="text-gray-600 mt-4 max-w-[540px]"
                style={{ fontSize: 16, lineHeight: 1.5 }}
              >
                {subtitle}
              </p>
            )}
            {(() => {
              const items = toolName && eyebrow ? [...(meta ?? []), eyebrow] : meta
              if (!items || items.length === 0) return null
              return (
                <div className="flex items-center gap-5 mt-6 text-[12px] uppercase tracking-[0.12em] font-bold text-gray-500 flex-wrap">
                  {items.map((m, i) => (
                    <span key={i} className="flex items-center gap-2">
                      {i > 0 && <span className="text-gray-300">•</span>}
                      <span>{m}</span>
                    </span>
                  ))}
                </div>
              )
            })()}
            {share && <div className="mt-5">{share}</div>}
          </div>
          {right}
        </div>
      </div>
    </section>
  )
}
